import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaBookOpen, FaCalendarAlt, FaNewspaper, FaList } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiGet, getEvents } from '../../services/api';
import { getTranslatedText } from '../../utils/translationHelper';
import '../styles/SearchDialog.css';
import { isEventActive } from '../../utils/eventDates';
import DOMPurify from 'dompurify'

const translations = {
  kz: {
    placeholder: 'Кітап, іс-шара, жаңалық...',
    all: 'Барлығы',
    books: 'Кітаптар',
    events: 'Іс-шаралар',
    news: 'Жаңалықтар',
    noResults: 'Нәтижелер табылмады',
    searchHint: 'Іздеу үшін мәтінді енгізіңіз',
    author: 'Автор',
    loading: 'Іздеу...',
  },
  ru: {
    placeholder: 'Книги, мероприятия, новости...',
    all: 'Все',
    books: 'Книги',
    events: 'Мероприятия',
    news: 'Новости',
    noResults: 'Результаты не найдены',
    searchHint: 'Введите текст для поиска',
    author: 'Автор',
    loading: 'Поиск...',
  },
  en: {
    placeholder: 'Books, events, news...',
    all: 'All',
    books: 'Books',
    events: 'Events',
    news: 'News',
    noResults: 'No results found',
    searchHint: 'Type to search',
    author: 'Author',
    loading: 'Searching...',
  },
};

function SearchDialog({ isOpen, onClose }) {
  const { currentLanguage } = useLanguage();
  const t = useMemo(() => translations[currentLanguage] || translations.ru, [currentLanguage]);

  const safeText = useCallback((value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && value.translations) {
      return getTranslatedText(value, currentLanguage);
    }
    return '';
  }, [currentLanguage]);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ books: [], events: [], news: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults({ books: [], events: [], news: [], total: 0 });
      setSearched(false);
      setLoading(false);
      setActiveTab('all');
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const performSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setResults({ books: [], events: [], news: [], total: 0 });
      setSearched(false);
      return;
    }

    setLoading(true);
    try {
      const response = await apiGet('/api/search', {
        params: { q: q.trim(), lang: currentLanguage },
        auth: false,
      });
      if (response.ok && response.data) {
        const data = response.data;
        // Search results may omit structured dates; use the canonical event records.
        const eventResponse = data.events?.length ? await getEvents() : null;
        const activeIds = new Set(
          (eventResponse?.events || [])
            .filter(event => isEventActive(event))
            .map(event => String(event.id))
        );
        const events = (data.events || []).filter(event => activeIds.has(String(event.id)));
        const books = data.books || [];
        const news = data.news || [];
        setResults({ ...data, books, news, events, total: books.length + news.length + events.length });
      } else {
        setResults({ books: [], events: [], news: [], total: 0 });
      }
      setSearched(true);
    } catch (err) {
      setResults({ books: [], events: [], news: [], total: 0 });
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, [currentLanguage]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(val);
    }, 350);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      performSearch(query);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults({ books: [], events: [], news: [], total: 0 });
    setSearched(false);
    inputRef.current?.focus();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const hasAnyCategory = results.books.length > 0 || results.events.length > 0 || results.news.length > 0;

  const tabs = useMemo(() => [
    { key: 'all', icon: FaList, label: t.all },
    { key: 'books', icon: FaBookOpen, label: t.books, count: results.books.length },
    { key: 'events', icon: FaCalendarAlt, label: t.events, count: results.events.length },
    { key: 'news', icon: FaNewspaper, label: t.news, count: results.news.length },
  ], [t, results]);

  const showSidebar = searched && hasAnyCategory;

  const filteredContent = useMemo(() => {
    if (activeTab === 'all') {
      return ['books', 'events', 'news'].
        filter(cat => results[cat]?.length > 0);
    }
    return results[activeTab]?.length > 0 ? [activeTab] : [];
  }, [activeTab, results]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : currentLanguage === 'kz' ? 'kk-KZ' : 'ru-RU');
    } catch {
      return dateStr;
    }
  };

  const renderCategory = (category) => {
    switch (category) {
      case 'books':
        return (
          <div className="search-category" key="books">
            <div className="search-items">
              {results.books.map((book) => (
                <div
                  key={`book-${book.id}`}
                  className="search-item search-item-book"
                  onClick={() => navigate(`/book/${book.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/book/${book.id}`);
                    }
                  }}
                >
                  {book.img_url && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${book.img_url}`}
                      alt=""
                      className="search-item-img"
                    />
                  )}
                  <div className="search-item-info">
                    <div className="search-item-title">{safeText(book.title)}</div>
                    <div className="search-item-meta">
                      {book.author && `${t.author}: ${safeText(book.author)}`}
                      {book.year && ` · ${book.year}`}
                    </div>
                    {book.annotation && (
                      <div className="search-item-desc">{safeText(book.annotation)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'events':
        return (
          <div className="search-category" key="events">
            <div className="search-items">
              {results.events.map((event) => (
                <div
                  key={`event-${event.id}`}
                  className="search-item search-item-event"
                  onClick={() => navigate(`/events/${event.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/events/${event.id}`);
                    }
                  }}
                >
                  {event.img_url && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${event.img_url}`}
                      alt=""
                      className="search-item-img"
                    />
                  )}
                  <div className="search-item-info">
                    <div className="search-item-title">{safeText(event.eventName)}</div>
                    <div className="search-item-meta">{event.date}</div>
                    {event.place && (
                      <div className="search-item-desc">{safeText(event.place)}</div>
                    )}
                    {event.description && (
                      <div className="search-item-desc">{safeText(event.description)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'news':
        return (
          <div className="search-category" key="news">
            <div className="search-items">
              {results.news.map((newsItem) => (
                <div
                  key={`news-${newsItem.id}`}
                  className="search-item search-item-news"
                  onClick={() => navigate(`/news/${newsItem.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/news/${newsItem.id}`);
                    }
                  }}
                >
                  {newsItem.img_url && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${newsItem.img_url}`}
                      alt=""
                      className="search-item-img"
                    />
                  )}
                  <div className="search-item-info">
                    <div className="search-item-title">{safeText(newsItem.title)}</div>
                    <div className="search-item-meta">{formatDate(newsItem.date)}</div>
                    {newsItem.description && (
                      <div className="search-item-desc">
                        <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(safeText(newsItem.description), { ALLOWED_TAGS: ['p'], ALLOWED_ATTR: [] })}} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="search-overlay" onClick={handleOverlayClick}>
      <div className="search-dialog">
        <div className="search-dialog-header">
          <div className="search-input-wrapper">
            <FaSearch className="search-input-icon" />
            <input
              ref={inputRef}
              type="text"
              className="search-dialog-input"
              placeholder={t.placeholder}
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
            {query && (
              <button className="search-clear-btn" onClick={handleClear} type="button">
                <FaTimes />
              </button>
            )}
          </div>
          <button className="search-close-btn" onClick={onClose} type="button">
            <FaTimes />
          </button>
        </div>

        <div className="search-body-wrapper">
          {showSidebar && (
            <div className="search-sidebar">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`search-sidebar-btn${activeTab === tab.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                  type="button"
                >
                  <tab.icon className="search-sidebar-btn-icon" />
                  <span className="search-sidebar-btn-label">{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className="search-sidebar-btn-count">{tab.count}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="search-dialog-body">
            {loading && (
              <div className="search-loading">
                <div className="search-spinner" />
                {t.loading}
              </div>
            )}

            {!loading && !searched && !query && (
              <div className="search-hint">{t.searchHint}</div>
            )}

            {!loading && searched && !hasAnyCategory && (
              <div className="search-no-results">{t.noResults}</div>
            )}

            {!loading && hasAnyCategory && (
              <div className="search-results">
                {filteredContent.map((cat) => renderCategory(cat))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchDialog;
